import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { AttachedFile } from '../state/types';
import { clearDraft, loadDraft, saveDraft } from '../state/storage';
import { i18n } from '../i18n/nl';
import {
  iconAttach,
  iconClose,
  iconImage,
  iconSend,
  iconSmile,
} from './icons';

/**
 * <embrace-chat-composer> — invoer onderaan het paneel.
 *
 * Events:
 *   - user-submit  { text: string } — user stuurt een bericht
 *   - upload       { files: File[] } — user klikt op de paperclip en kiest bestanden
 *   - remove-staged { fileId: string } — user verwijdert een staged chip
 */
@customElement('embrace-chat-composer')
export class EmbraceChatComposer extends LitElement {
  @property({ type: String }) placeholder = i18n.composer.placeholderDefault;
  @property({ type: String }) mode: 'text' | 'email' = 'text';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Number }) maxLength: number | null = null;
  @property({ type: String }) disabledHint = '';
  /** Permanente sub-tekst onder de composer wanneer hij NIET disabled is
   *  (zelfde regio als `disabledHint`). Gebruikt voor de AI-disclaimer. */
  @property({ type: String }) footnote = '';
  /**
   * Of de emoji-knop überhaupt bestaat. `false` (config `enableEmoji: false`)
   * → de knop verdwijnt volledig uit de tools-rij. Default `true`.
   */
  @property({ type: Boolean }) enableEmoji = true;
  /**
   * Of de bijlage-knop überhaupt bestaat. Alleen flows met een echte
   * upload-bestemming zetten 'm aan (de AAVA-intake). Voor de chat met een
   * medewerker is er geen upload-endpoint, dus daar verdwijnt de knop
   * volledig (WI 164914 / AC3 — niet geïmplementeerd). Default `true`.
   */
  @property({ type: Boolean }) enableAttach = true;
  /**
   * Of het gesprek "staat": details ingevuld + concrete uitwisseling met de
   * AI of een medewerker. `false` → de emoji- én bijlage-knop zijn zichtbaar
   * maar disabled tot het zover is (WI 164916 emoji, WI 164914 bijlage).
   * Default `true` zodat een losse mount van de composer zich als vanouds
   * gedraagt; panel-views zet 'm expliciet per flow-stap.
   */
  @property({ type: Boolean }) established = true;
  @property({ attribute: false }) stagedFiles: AttachedFile[] = [];

  @state() private value = '';
  @state() private emojiOpen = false;
  @state() private emojiLoaded = false;

  override connectedCallback(): void {
    super.connectedCallback();
    // Resume eventueel halfgetypt bericht uit localStorage. Geen-op als
    // er niks was — visitor ziet dan een leeg veld zoals voorheen.
    const draft = loadDraft();
    if (draft) this.value = draft;
    document.addEventListener('mousedown', this.onDocumentClick, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.onDocumentClick, true);
  }

  /**
   * Sluit de emoji-popover als er buiten geklikt wordt. composedPath()
   * loopt door Shadow-DOM-grenzen heen — anders zou event.target altijd
   * de chat-host wijzen en zouden klikken in onze eigen Shadow root als
   * "buiten" tellen.
   */
  private readonly onDocumentClick = (e: MouseEvent): void => {
    if (!this.emojiOpen) return;
    const path = e.composedPath();
    const isInside = path.some(
      (el) =>
        el instanceof HTMLElement &&
        (el.classList?.contains('emoji-host') ||
          el.tagName === 'EMOJI-PICKER' ||
          (el.classList?.contains('tool') &&
            el.getAttribute('aria-label') === 'Emoji invoegen')),
    );
    if (!isInside) this.emojiOpen = false;
  };

  private async toggleEmoji(): Promise<void> {
    // Defensief: knop is al ?disabled/verborgen in deze gevallen, maar een
    // programmatic/keyboard-race mag de popover niet alsnog openen.
    if (!this.enableEmoji || !this.established || this.disabled) return;
    if (this.emojiOpen) {
      this.emojiOpen = false;
      return;
    }
    this.emojiOpen = true;
    if (!this.emojiLoaded) {
      // Lazy-import: het ~13KB emoji-picker-element + IndexedDB-data laden
      // pas wanneer de bezoeker daadwerkelijk emoji's wil. Initial bundle
      // blijft schoon. Bij CSP-block / netwerkfout: popover sluiten i.p.v.
      // een lege popover voor altijd te tonen.
      try {
        await import('emoji-picker-element');
        this.emojiLoaded = true;
      } catch (err) {
        console.warn('[Composer] Emoji-picker kon niet geladen worden:', err);
        this.emojiOpen = false;
      }
    }
  }

  private onEmojiClick(e: CustomEvent<{ unicode: string }>): void {
    const emoji = e.detail.unicode;
    if (!emoji) return;
    const ta = this.renderRoot.querySelector('textarea');
    if (!ta) {
      this.value += emoji;
      saveDraft(this.value);
      return;
    }
    // Insertion op cursor-positie (i.p.v. blind appenden) zodat de bezoeker
    // emoji's midden in een halfgetypte zin kan plaatsen.
    const start = ta.selectionStart ?? this.value.length;
    const end = ta.selectionEnd ?? this.value.length;
    const next = this.value.slice(0, start) + emoji + this.value.slice(end);
    this.value = next;
    saveDraft(next);
    // Cursor net na de ingevoegde emoji — wachten op Lit-render zodat de
    // textarea-DOM up-to-date is voordat we selectionStart zetten.
    requestAnimationFrame(() => {
      const after = this.renderRoot.querySelector('textarea');
      if (after) {
        const pos = start + emoji.length;
        after.focus();
        after.setSelectionRange(pos, pos);
      }
    });
  }

  static override styles = css`
    :host {
      display: block;
    }

    .composer {
      border-top: 1px solid var(--ec-border);
      padding: 10px 12px 12px;
      background: var(--ec-background);
    }
    .composer.disabled {
      background: var(--ec-surface-sunken);
    }

    .staged {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 4px 2px 8px;
    }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: var(--ec-surface-sunken);
      border: 1px solid var(--ec-border);
      border-radius: 9999px;
      padding: 4px 6px 4px 10px;
      font: 500 12px/1.2 var(--ec-font-family);
      color: var(--ec-text);
      max-width: 220px;
    }
    .chip .fn {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .chip button {
      border: 0;
      background: transparent;
      color: #5E5F62;
      width: 20px;
      height: 20px;
      border-radius: 9999px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .chip button:hover {
      background: rgba(0,0,0,.06);
      color: var(--ec-text);
    }

    /* Outer frame met border + focus-within state. Bevat de textarea
       bovenaan + de actions-rij onderaan zodat alles als één invoerveld
       voelt. */
    .row {
      display: flex;
      flex-direction: column;
      gap: 4px;
      border: 1px solid var(--ec-border);
      border-radius: var(--ec-radius-input);
      padding: 8px 12px 6px;
      background: #fff;
      transition: border-color var(--ec-duration-fast) var(--ec-ease-smooth),
                  box-shadow var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .row:focus-within {
      border-color: var(--ec-primary);
      box-shadow: 0 0 0 3px rgba(12,28,71,.1);
    }
    .composer.disabled .row {
      background: var(--ec-surface-sunken);
      border-style: dashed;
      cursor: not-allowed;
    }

    textarea {
      width: 100%;
      border: 0;
      outline: 0;
      resize: none;
      font: 400 14px/20px var(--ec-font-family);
      color: var(--ec-text);
      padding: 4px 0;
      max-height: 120px;
      background: transparent;
      /* border-box: style.height is totale hoogte (incl. padding). Voorkomt
         dat de auto-resize-logica padding dubbel telt — scrollHeight bevat
         al de padding, dus zonder border-box zou setting style.height =
         scrollHeight de textarea 16px te hoog maken (= padding-dubbel). */
      box-sizing: border-box;
    }
    textarea::placeholder { color: #8C8D8F; }
    textarea:disabled { color: #8C8D8F; cursor: not-allowed; }

    /* Onder-de-textarea: tools links, send-knop rechts. */
    .actions {
      display: flex;
      align-items: center;
      gap: 2px;
    }
    .tools {
      display: flex;
      gap: 2px;
      flex: 1;
    }

    .tool {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: transparent;
      border: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #5E5F62;
      flex-shrink: 0;
    }
    .tool:hover { background: rgba(0,0,0,.04); color: var(--ec-text); }
    .tool:disabled { cursor: not-allowed; opacity: .45; }
    .tool.active { color: var(--ec-primary); background: rgba(12,28,71,.06); }

    /* Emoji-popover floats above the row */
    .emoji-host {
      position: absolute;
      bottom: calc(100% + 6px);
      left: 12px;
      right: 12px;
      max-width: 360px;
      z-index: 5;
    }
    .composer { position: relative; }
    /* Theme emoji-picker-element to onze design-tokens. Custom-properties
       worden door het component zelf gerespecteerd. */
    emoji-picker {
      width: 100%;
      height: 320px;
      --background: #fff;
      --border-color: var(--ec-border);
      --border-radius: 14px;
      --button-active-background: rgba(12,28,71,.1);
      --button-hover-background: rgba(12,28,71,.05);
      --indicator-color: var(--ec-primary);
      --input-border-color: var(--ec-border);
      --input-border-radius: 8px;
      --input-font-color: var(--ec-text);
      --input-placeholder-color: #8C8D8F;
      --num-columns: 8;
      box-shadow: var(--ec-shadow-md);
      border-radius: 14px;
    }

    .send {
      width: 32px;
      height: 32px;
      border-radius: 9999px;
      background: var(--ec-primary);
      color: #fff;
      border: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .send:hover:not(:disabled) { background: #12265a; }
    .send:disabled { background: #E1E1E1; color: #8C8D8F; cursor: not-allowed; }
    .send:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }

    .foot {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 4px 0;
      font: 500 11px/1.4 var(--ec-font-family);
      color: #8C8D8F;
    }
  `;

  override render() {
    const canSend = !this.disabled && (this.value.trim().length > 0 || this.stagedFiles.length > 0);

    // Disabled → uitleg-hint (bv. "…is aan het denken…"); enabled → de
    // optionele footnote (AI-disclaimer). Beide renderen in dezelfde .foot-regio.
    const footText = this.disabled
      ? this.disabledHint || i18n.composer.disabledHintEnded
      : this.footnote || null;

    return html`
      <div class=${`composer${this.disabled ? ' disabled' : ''}`} part="composer">
        ${this.stagedFiles.length > 0
          ? html`
              <div class="staged">
                ${this.stagedFiles.map(
                  (f) => html`
                    <div class="chip">
                      ${f.kind === 'image' ? iconImage() : iconAttach()}
                      <span class="fn">${f.name}</span>
                      <button
                        aria-label=${`Verwijder ${f.name}`}
                        @click=${() => this.removeStaged(f.id)}
                      >
                        ${iconClose(12)}
                      </button>
                    </div>
                  `,
                )}
              </div>
            `
          : nothing}

        ${this.emojiOpen && this.enableEmoji && this.established
          ? html`
              <div class="emoji-host" part="emoji-popover">
                ${this.emojiLoaded
                  ? html`<emoji-picker
                      locale="nl"
                      @emoji-click=${this.onEmojiClick}
                    ></emoji-picker>`
                  : nothing}
              </div>
            `
          : nothing}

        <div class="row">
          <textarea
            part="composer-input"
            rows="1"
            .value=${this.value}
            placeholder=${this.placeholder}
            inputmode=${this.mode === 'email' ? 'email' : 'text'}
            autocapitalize=${this.mode === 'email' ? 'off' : 'sentences'}
            autocomplete=${this.mode === 'email' ? 'email' : 'off'}
            maxlength=${this.maxLength ?? ''}
            ?disabled=${this.disabled}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
          ></textarea>
          <div class="actions">
            <div class="tools">
              ${this.enableAttach
                ? html`
                    <button
                      class="tool"
                      title=${i18n.composer.btnAttach}
                      aria-label=${i18n.composer.btnAttach}
                      ?disabled=${this.disabled || !this.established}
                      @click=${this.openFilePicker}
                    >
                      ${iconAttach()}
                    </button>
                  `
                : nothing}
              ${this.enableEmoji
                ? html`
                    <button
                      class=${`tool${this.emojiOpen ? ' active' : ''}`}
                      title=${i18n.composer.btnEmoji}
                      aria-label=${i18n.composer.btnEmoji}
                      aria-expanded=${this.emojiOpen}
                      ?disabled=${this.disabled || !this.established}
                      @click=${this.toggleEmoji}
                    >
                      ${iconSmile()}
                    </button>
                  `
                : nothing}
            </div>
            <button
              part="composer-send"
              class="send"
              aria-label=${i18n.composer.btnSend}
              ?disabled=${!canSend}
              @click=${this.submit}
            >
              ${iconSend()}
            </button>
          </div>
        </div>

        ${footText
          ? html`<div class="foot"><span>${footText}</span></div>`
          : nothing}
      </div>
    `;
  }

  private onInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    this.value = target.value;
    saveDraft(target.value);
    target.style.height = 'auto';
    target.style.height = `${Math.min(120, target.scrollHeight)}px`;
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submit();
    }
  }

  private submit() {
    const text = this.value.trim();
    const hasStaged = this.stagedFiles.length > 0;
    if (this.disabled || (!text && !hasStaged)) return;

    this.dispatchEvent(
      new CustomEvent('user-submit', {
        detail: { text, stagedFileIds: this.stagedFiles.map((f) => f.id) },
        bubbles: true,
        composed: true,
      }),
    );
    this.value = '';
    clearDraft();
    this.emojiOpen = false;
    const ta = this.renderRoot.querySelector('textarea');
    if (ta) ta.style.height = 'auto';
  }

  private openFilePicker() {
    // Defensief: knop is al verborgen/?disabled in deze gevallen, maar een
    // programmatic/keyboard-race mag de file-picker niet alsnog openen.
    if (!this.enableAttach || this.disabled || !this.established) return;
    this.dispatchEvent(
      new CustomEvent('open-file-picker', { bubbles: true, composed: true }),
    );
  }

  private removeStaged(id: string) {
    this.dispatchEvent(
      new CustomEvent('remove-staged', {
        detail: { fileId: id },
        bubbles: true,
        composed: true,
      }),
    );
  }


}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat-composer': EmbraceChatComposer;
  }
}
